// Settings — v1.1 §9. Built in Step 9.
//
// This file is the dispatcher and nothing else. The eight sections live in
// `routes/settings/`, one module each, so that adding a setting is an edit to
// one small file rather than to a growing page.
//
// WHAT LANDS HERE (§3.10), AND WHERE EACH PART WENT
// =================================================
//   /settings → SettingsPage                    all eight of its settings, below
//   /brain/memory (config half)                 System Configuration
//   /offers (CRUD half), /campaigns/register    Offers
//   IntegrationHealthWidget (config half)       Infrastructure
//
// The eight settings on the v1 page, and their new homes:
//
//   Connected Meta Ad Accounts   → Connections
//   Business Units               → Offers
//   Networks                     → Offers
//   Notification Preferences     → My Settings
//   Onboarding Tour reset        → My Settings
//   Cost Alert Threshold         → Infrastructure
//   Claude API Hard Cap          → Infrastructure
//   Audit Log                    → System Configuration
//
// Every one of them is reachable here, which is Step 9's Definition of Done and
// the test the Founder called the one that matters. None of the sources is
// deleted or unlinked: `/settings`, `/brain/memory`, `/offers` and
// `/campaigns/register` all still resolve and still work (R2, R3, R9).
//
// THE SECTION INDEX IS INSIDE THE SURFACE, ON PURPOSE
// ===================================================
// `surfaces/settings.md` line 9 sanctions two shapes — "Y-axis within Settings
// **or** a settings X-axis" — and this build takes the first. `areas.js`
// declares four Settings tabs and §9 names eight; the file is frozen tonight,
// so the four sections it has no entry for would otherwise be reachable only by
// typing a URL. The index in `SectionIndex.jsx` carries all of them and is
// rendered on every section. (Nine since 2026-10-29: **People**, added under
// §9.1's "unless a real need emerges" clause — there was no way to create an
// account at all. `surfaces/settings.md` records it.) The four X-axis tabs still work and now behave as
// deep links into it. The reasoning is set out in full in `settings/sections.js`.
//
// A1 — WHERE DIAGNOSTICS LIVE
// ===========================
// `MIGRATION_MAP.md` sends the Tiny Tim cost tile and tool history to
// "Operations/Settings diagnostics", and neither §7 nor §9 names a diagnostics
// surface. This build carries through the reading the shell's stub proposed:
// Settings → Infrastructure, alongside integration and scheduler health. §9
// already owns infrastructure, and §7 is about what the company is doing NOW,
// which cost accounting is not. The Founder confirms or overrides in the
// morning; it is one entry in `settings/sections/registry.js` either way.
import React from "react";

import PageHead from "../components/PageHead";
import { useShell } from "../ShellContext";
import SectionIndex from "./settings/SectionIndex";
import { isFounder, sectionForTab } from "./settings/sections";
import SECTION_COMPONENTS from "./settings/sections/registry";
import "./settings/settings.css";

export default function SettingsArea() {
  const { tab, user } = useShell();
  const section = sectionForTab(tab);
  const Section = SECTION_COMPONENTS[section.id];

  // Founder gating is navigation, not access control (R5). A non-founder who
  // types `/v2/settings/models` is not redirected — that would be a second,
  // weaker gate that looks like the real one. They get the section frame and a
  // stated refusal, and every endpoint behind it is refused by the server
  // regardless of what this component renders.
  const refused = section.founderOnly && !isFounder(user);

  return (
    <>
      <PageHead
        title={section.label}
        subtitle={
          refused
            ? "This section is founder-only."
            : "Configure what Cipher needs to operate."
        }
      />

      <div className="set-wrap">
        <SectionIndex user={user} />

        <div className="set-body">
          {refused ? (
            <div className="state set-state" role="status">
              <strong>Founder only.</strong>{" "}
              <span className="dim">
                This section is not available to your role, and the endpoints behind it are
                refused by the server independently of what is shown here.
              </span>
            </div>
          ) : (
            <Section user={user} />
          )}
        </div>
      </div>
    </>
  );
}
